import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, CheckCircle, XCircle, Eye, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BandejaRevision = () => {
  const navigate = useNavigate();
  
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
  const [observaciones, setObservaciones] = useState('');

  useEffect(() => {
    fetchActividades();
  }, []);

  const fetchActividades = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/actividades?estado=revision`,
        { withCredentials: true }
      );
      setActividades(data);
    } catch (error) {
      console.error('Error fetching actividades:', error);
      toast.error('Error al cargar actividades');
    } finally {
      setLoading(false);
    }
  };

  const handleAprobar = async (actividadId) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/actividades/${actividadId}/estado`,
        'aprobado',
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      toast.success('Actividad aprobada exitosamente');
      fetchActividades();
      setActividadSeleccionada(null);
    } catch (error) {
      console.error('Error approving actividad:', error);
      toast.error('Error al aprobar actividad');
    }
  };

  const handleRechazar = async (actividadId) => {
    if (!observaciones.trim()) {
      toast.error('Por favor agrega observaciones para el rechazo');
      return;
    }

    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/actividades/${actividadId}/estado`,
        'rechazado',
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      toast.success('Actividad rechazada');
      fetchActividades();
      setActividadSeleccionada(null);
      setObservaciones('');
    } catch (error) {
      console.error('Error rejecting actividad:', error);
      toast.error('Error al rechazar actividad');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Bandeja de Revisión</h1>
              <p className="text-sm text-muted-foreground">Revisar y aprobar actividades didácticas</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de Actividades en Revisión */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  En Revisión ({actividades.length})
                </CardTitle>
                <CardDescription>Actividades pendientes de aprobación</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto"></div>
                  </div>
                ) : actividades.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No hay actividades en revisión
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
                    {actividades.map((actividad) => (
                      <Card
                        key={actividad.actividad_id}
                        className={`cursor-pointer transition-all border-2 ${
                          actividadSeleccionada?.actividad_id === actividad.actividad_id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => setActividadSeleccionada(actividad)}
                        data-testid={`actividad-${actividad.actividad_id}`}
                      >
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm line-clamp-2">{actividad.titulo}</CardTitle>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">{actividad.tipo_actividad}</Badge>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Panel de Detalle y Revisión */}
          <div className="lg:col-span-2">
            {!actividadSeleccionada ? (
              <Card className="border-2 border-dashed border-border">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Eye className="h-16 w-16 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">Selecciona una actividad</p>
                  <p className="text-muted-foreground text-center">Haz clic en una actividad de la lista para revisar su contenido</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Información Básica */}
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-2xl mb-2">{actividadSeleccionada.titulo}</CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge>{actividadSeleccionada.tipo_actividad}</Badge>
                          <Badge variant="outline">{actividadSeleccionada.estado}</Badge>
                          {actividadSeleccionada.privada && <Badge variant="secondary">Privada</Badge>}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Descripción</h4>
                      <p className="text-muted-foreground">{actividadSeleccionada.descripcion || 'Sin descripción'}</p>
                    </div>
                    {actividadSeleccionada.instrucciones && (
                      <div>
                        <h4 className="font-semibold mb-2">Instrucciones</h4>
                        <p className="text-muted-foreground">{actividadSeleccionada.instrucciones}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Configuración de Evaluación */}
                <Card>
                  <CardHeader>
                    <CardTitle>Configuración de Evaluación</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        {actividadSeleccionada.permite_nota ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="text-sm">Calificación numérica</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {actividadSeleccionada.permite_logro ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="text-sm">Mide nivel de logro</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {actividadSeleccionada.permite_intentos ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="text-sm">Limita intentos ({actividadSeleccionada.max_intentos})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {actividadSeleccionada.mostrar_retroalimentacion_inmediata ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="text-sm">Retroalimentación inmediata</span>
                      </div>
                    </div>
                    {actividadSeleccionada.permite_logro && actividadSeleccionada.indicador_evaluacion && (
                      <div className="mt-4 space-y-4 border-t border-border pt-4">
                        <div>
                          <h4 className="font-semibold mb-2">Indicador de evaluación</h4>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {actividadSeleccionada.indicador_evaluacion}
                          </p>
                        </div>
                        {actividadSeleccionada.rubrica_logro && (
                          <div className="grid gap-3">
                            {['Inicial', 'Intermedio', 'Avanzado'].map((nivel) => (
                              <div key={nivel} className="rounded-lg border border-border p-3">
                                <h5 className="font-semibold mb-1">{nivel}</h5>
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                  {actividadSeleccionada.rubrica_logro[nivel] || 'Sin descriptor definido'}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Contenido (Preguntas) */}
                {actividadSeleccionada.tipo_actividad === 'seleccion_multiple' && actividadSeleccionada.contenido_json?.preguntas && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Preguntas ({actividadSeleccionada.contenido_json.preguntas.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {actividadSeleccionada.contenido_json.preguntas.map((pregunta, index) => (
                        <Card key={pregunta.id} className="border-2">
                          <CardHeader>
                            <CardTitle className="text-lg">Pregunta {index + 1}</CardTitle>
                            <p className="text-muted-foreground mt-2">{pregunta.texto}</p>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            {pregunta.opciones.map((opcion, opcionIndex) => (
                              <div 
                                key={opcionIndex} 
                                className={`p-3 rounded-lg border-2 ${
                                  opcionIndex === pregunta.respuesta_correcta 
                                    ? 'border-green-500 bg-green-50 dark:bg-green-950' 
                                    : 'border-border'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {opcionIndex === pregunta.respuesta_correcta && (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                  )}
                                  <span>{opcion}</span>
                                </div>
                              </div>
                            ))}
                            {pregunta.retroalimentacion && (
                              <p className="text-sm text-muted-foreground italic mt-2">
                                Retroalimentación: {pregunta.retroalimentacion}
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Observaciones para Rechazo */}
                <Card>
                  <CardHeader>
                    <CardTitle>Observaciones</CardTitle>
                    <CardDescription>Agrega comentarios si decides rechazar la actividad</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <textarea
                      className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Escribe tus observaciones aquí..."
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      data-testid="textarea-observaciones"
                    />
                  </CardContent>
                </Card>

                {/* Botones de Acción */}
                <div className="flex gap-2 justify-end sticky bottom-4 bg-background/80 backdrop-blur-xl p-4 rounded-lg border border-border">
                  <Button
                    variant="destructive"
                    onClick={() => handleRechazar(actividadSeleccionada.actividad_id)}
                    data-testid="btn-rechazar"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Rechazar
                  </Button>
                  <Button
                    onClick={() => handleAprobar(actividadSeleccionada.actividad_id)}
                    data-testid="btn-aprobar"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Aprobar
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BandejaRevision;
